import { forwardRef, type ReactNode, type TextareaHTMLAttributes } from 'react';
import { clsx } from '../clsx';
import { useFieldIds } from '../hooks';
import { FieldFrame } from './FieldFrame';
import styles from './Textarea.module.css';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  labelHidden?: boolean | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  optional?: boolean | undefined;
  labelEnd?: ReactNode | undefined;
  /** Grows with the content up to `maxRows` instead of scrolling. */
  autoGrow?: boolean | undefined;
  tone?: 'card' | 'sunken' | undefined;
}

/** Multi-line field with the same chrome as the text field (notes, reasons, message bodies). */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    label,
    labelHidden,
    hint,
    error,
    invalid,
    required,
    optional,
    labelEnd,
    autoGrow = false,
    tone,
    className,
    id,
    readOnly,
    disabled,
    rows = 3,
    onInput,
    ...rest
  },
  ref,
) {
  const ids = useFieldIds(id, { hint: Boolean(hint), error: Boolean(error) });
  return (
    <FieldFrame
      htmlFor={ids.inputId}
      label={label}
      labelHidden={labelHidden}
      required={required}
      optional={optional}
      labelEnd={labelEnd}
      hint={hint}
      error={error}
      hintId={ids.hintId}
      errorId={ids.errorId}
      invalid={invalid}
      readOnly={readOnly}
      disabled={disabled}
      multiline
      tone={tone}
      className={className}
    >
      <textarea
        ref={ref}
        id={ids.inputId}
        rows={rows}
        className={clsx(styles.textarea, autoGrow && styles.autoGrow)}
        aria-invalid={error || invalid ? true : undefined}
        aria-describedby={ids.describedBy}
        aria-required={required || undefined}
        readOnly={readOnly}
        disabled={disabled}
        onInput={(event) => {
          if (autoGrow) {
            const element = event.currentTarget;
            element.style.height = 'auto';
            element.style.height = `${element.scrollHeight}px`;
          }
          onInput?.(event);
        }}
        {...rest}
      />
    </FieldFrame>
  );
});
